import { useEffect, useRef, useState } from "react";

const MIME_CANDIDATES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];

const pickMime = () => {
  if (typeof window === "undefined" || !window.MediaRecorder?.isTypeSupported) return "";
  return MIME_CANDIDATES.find((m) => window.MediaRecorder.isTypeSupported(m)) || "";
};

// Tiny silent WAV — played inside the mic tap so iOS allows auto-play of the reply later.
export const silentWavUrl = () => {
  const n = 800, buf = new ArrayBuffer(44 + n), v = new DataView(buf);
  const w = (o, s) => [...s].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
  w(0, "RIFF"); v.setUint32(4, 36 + n, true); w(8, "WAVE"); w(12, "fmt ");
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, 8000, true); v.setUint32(28, 8000, true); v.setUint16(32, 1, true); v.setUint16(34, 8, true);
  w(36, "data"); v.setUint32(40, n, true);
  for (let i = 0; i < n; i++) v.setUint8(44 + i, 128);
  return URL.createObjectURL(new Blob([buf], { type: "audio/wav" }));
};

export function useVoiceRecorder({ onAudio, maxMs = 60000 }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const recRef = useRef(null);
  const timers = useRef([]);
  const onAudioRef = useRef(onAudio);
  onAudioRef.current = onAudio;
  const supported =
    typeof window !== "undefined" && !!navigator.mediaDevices?.getUserMedia && !!window.MediaRecorder;

  const clearTimers = () => { timers.current.forEach(clearInterval); timers.current = []; };

  const stop = () => {
    clearTimers();
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
  };

  const start = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mime = pickMime();
    const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    const chunks = [];
    rec.ondataavailable = (e) => e.data?.size && chunks.push(e.data);
    rec.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      setRecording(false);
      const blob = new Blob(chunks, { type: rec.mimeType || mime || "audio/webm" });
      if (blob.size > 0) onAudioRef.current(blob);
    };
    recRef.current = rec;
    rec.start();
    setElapsed(0);
    setRecording(true);
    const t0 = Date.now();
    timers.current.push(setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 250));
    timers.current.push(setInterval(() => Date.now() - t0 >= maxMs && stop(), 500));
  };

  useEffect(() => () => { clearTimers(); recRef.current?.state === "recording" && recRef.current.stop(); }, []);

  return { supported, recording, elapsed, start, stop };
}
